import { Injectable, computed, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatBotService, ChatStreamChunk } from './chat-bot.service';
import { ChatActionsService } from './chat-actions.service';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error';
export type ActionState = 'pending' | 'confirming' | 'confirmed' | 'dismissed' | 'failed';

export interface PendingAction {
  pendingActionId: string;
  summary: string;
  state: ActionState;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  citations: string[];
  actions: PendingAction[];
  messageId?: string;
  rating?: 'up' | 'down';
  status: 'streaming' | 'done' | 'error';
  error?: string;
  createdAt: Date;
}

/**
 * Holds the chat conversation for the whole app so the header panel and the
 * full page share one conversation and one hub connection.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatSessionService {
  private chat = inject(ChatBotService);
  private actionsApi = inject(ChatActionsService);
  private streamSub: Subscription | null = null;
  private connecting: Promise<void> | null = null;
  private idCounter = 0;

  readonly messages = signal<ChatMessage[]>([]);
  readonly sending = signal(false);
  readonly connectionState = signal<ConnectionState>('idle');
  readonly conversationId = signal<string | null>(null);
  /** Set when a reply finishes; the launcher clears it once the panel is seen. */
  readonly unread = signal(false);

  readonly isEmpty = computed(() => this.messages().length === 0);

  ensureConnected(): Promise<void> {
    if (this.connectionState() === 'connected') return Promise.resolve();
    if (this.connecting) return this.connecting;

    this.connectionState.set('connecting');
    this.connecting = this.chat.connect()
      .then(() => this.connectionState.set('connected'))
      .catch(() => this.connectionState.set('error'))
      .finally(() => (this.connecting = null));
    return this.connecting;
  }

  async send(raw: string) {
    const text = raw.trim();
    if (!text || this.sending()) return;

    await this.ensureConnected();
    if (this.connectionState() !== 'connected') return;

    this.messages.update((m) => [
      ...m,
      this.createMessage('user', text, 'done'),
      this.createMessage('assistant', '', 'streaming')
    ]);
    this.sending.set(true);

    this.streamSub = this.chat.sendMessage(text, this.conversationId()).subscribe({
      next: (chunk) => this.handleChunk(chunk),
      error: (err) => {
        this.updateLast((last) => ({ ...last, status: 'error', error: err?.message ?? 'Something went wrong' }));
        this.finish();
      },
      complete: () => {
        this.updateLast((last) => (last.status === 'streaming' ? { ...last, status: 'done' } : last));
        this.unread.set(true);
        this.finish();
      }
    });
  }

  stop() {
    if (!this.streamSub) return;
    this.streamSub.unsubscribe();
    this.updateLast((last) => (last.status === 'streaming' ? { ...last, status: 'done' } : last));
    this.finish();
  }

  newConversation() {
    this.stop();
    this.messages.set([]);
    this.conversationId.set(null);
    this.unread.set(false);
  }

  confirm(message: ChatMessage, action: PendingAction) {
    const conversationId = this.conversationId();
    if (!conversationId) return;

    this.setActionState(message.id, action.pendingActionId, 'confirming');
    this.actionsApi.confirmAction(conversationId, action.pendingActionId).subscribe({
      next: () => this.setActionState(message.id, action.pendingActionId, 'confirmed'),
      error: () => this.setActionState(message.id, action.pendingActionId, 'failed')
    });
  }

  dismiss(message: ChatMessage, action: PendingAction) {
    this.setActionState(message.id, action.pendingActionId, 'dismissed');
  }

  rate(message: ChatMessage, good: boolean) {
    const conversationId = this.conversationId();
    if (!message.messageId || !conversationId || message.rating) return;

    this.updateMessage(message.id, (m) => ({ ...m, rating: good ? 'up' : 'down' }));
    this.actionsApi.submitFeedback(conversationId, message.messageId, good ? 0 : 1).subscribe({
      error: () => this.updateMessage(message.id, (m) => ({ ...m, rating: undefined }))
    });
  }

  private handleChunk(chunk: ChatStreamChunk) {
    switch (chunk.type) {
      case 'started':
        this.conversationId.set(chunk.conversationId!);
        break;
      case 'token':
        this.updateLast((last) => ({ ...last, text: last.text + (chunk.content ?? '') }));
        break;
      case 'citation':
        this.updateLast((last) => ({ ...last, citations: [...last.citations, chunk.content!] }));
        break;
      case 'action_proposal':
        this.updateLast((last) => ({
          ...last,
          actions: [...last.actions, { pendingActionId: chunk.pendingActionId!, summary: chunk.content!, state: 'pending' }]
        }));
        break;
      case 'error':
        this.updateLast((last) => ({ ...last, status: 'error', error: chunk.content }));
        break;
      case 'done':
        this.updateLast((last) => ({ ...last, messageId: chunk.messageId, status: last.status === 'error' ? 'error' : 'done' }));
        break;
    }
  }

  private finish() {
    this.streamSub = null;
    this.sending.set(false);
  }

  private createMessage(role: ChatMessage['role'], text: string, status: ChatMessage['status']): ChatMessage {
    return { id: `m${++this.idCounter}`, role, text, citations: [], actions: [], status, createdAt: new Date() };
  }

  private updateLast(fn: (m: ChatMessage) => ChatMessage) {
    this.messages.update((m) => {
      if (!m.length) return m;
      const copy = [...m];
      copy[copy.length - 1] = fn(copy[copy.length - 1]);
      return copy;
    });
  }

  private updateMessage(id: string, fn: (m: ChatMessage) => ChatMessage) {
    this.messages.update((m) => m.map((x) => (x.id === id ? fn(x) : x)));
  }

  private setActionState(messageId: string, pendingActionId: string, state: ActionState) {
    this.updateMessage(messageId, (m) => ({
      ...m,
      actions: m.actions.map((a) => (a.pendingActionId === pendingActionId ? { ...a, state } : a))
    }));
  }
}
